import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Plus, MoreHorizontal, Pencil, Trash2, CalendarIcon, Receipt, Upload, FileSpreadsheet,
  Wallet, Landmark, TrendingDown, TrendingUp, Sparkles, AlertTriangle, Zap, LineChart as LineChartIcon, Database, Briefcase,
  Target, Check, X as XIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend, ReferenceLine,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const BASE_CATEGORIES = ['Repair', 'Shipping', 'Accessories', 'Marketing', 'Other'];
const CATEGORY_COLORS = {
  Repair: 'bg-blue-500/10 text-blue-600',
  Shipping: 'bg-violet-500/10 text-violet-600',
  Accessories: 'bg-emerald-500/10 text-emerald-600',
  Marketing: 'bg-amber-500/10 text-amber-600',
  Other: 'bg-slate-500/10 text-slate-600',
};
// Per design guidelines: don't use red for expenses (brand color is red).
// Use warning/orange for expenses, success/green for profit.
const CHART_PALETTE = ['#F59E0B', '#0EA5E9', '#8B5CF6', '#10B981', '#64748B', '#EAB308'];

const emptyForm = { category: 'Repair', description: '', amount: '', date: new Date(), related_inventory_id: '', notes: '', payment_source: 'cash' };

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Google-Sheet / CSV import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('expenses'); // 'expenses' | 'balance'
  const [sheetUrl, setSheetUrl] = useState('');
  const [replaceAll, setReplaceAll] = useState(false);
  const [balanceYear, setBalanceYear] = useState(new Date().getFullYear());
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);

  // "Load my saved notes" (full Jan–May 2026 itemized dataset)
  const [loadNotesOpen, setLoadNotesOpen] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // Quick Expense widget
  const [quickAmount, setQuickAmount] = useState('');
  const [quickCategory, setQuickCategory] = useState('Repair');
  const [quickDescription, setQuickDescription] = useState('');
  const [quickSource, setQuickSource] = useState('cash');
  const [quickSaving, setQuickSaving] = useState(false);

  // User-added categories (persisted locally). Old version supported adding
  // a brand-new category right from the dropdown ("+ Add new category…").
  const CUSTOM_CATS_KEY = 'fliptrack_custom_categories';
  const [customCategories, setCustomCategories] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_CATS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_e) { return []; }
  });
  const [newCatDialogOpen, setNewCatDialogOpen] = useState(false);
  const [newCatTarget, setNewCatTarget] = useState('quick'); // 'quick' | 'form'
  const [newCatName, setNewCatName] = useState('');

  const openNewCategory = (target) => {
    setNewCatTarget(target);
    setNewCatName('');
    setNewCatDialogOpen(true);
  };

  const saveNewCategory = () => {
    const name = (newCatName || '').trim();
    if (!name) { toast.error('Enter a category name'); return; }
    if (name.length > 40) { toast.error('Keep it under 40 chars'); return; }
    setCustomCategories(prev => {
      const exists = prev.some(c => c.toLowerCase() === name.toLowerCase());
      const next = exists ? prev : [...prev, name];
      try { localStorage.setItem(CUSTOM_CATS_KEY, JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
    if (newCatTarget === 'quick') setQuickCategory(name);
    else setForm(f => ({ ...f, category: name }));
    setNewCatDialogOpen(false);
    toast.success(`Added category “${name}”`);
  };

  // Balance history (for Capital Timeline)
  const [balanceHistory, setBalanceHistory] = useState([]);

  // Capital goal
  const [goalData, setGoalData] = useState(null); // { goal, live_capital, daily_change, ... }
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalAmount, setGoalAmount] = useState('');
  const [goalDate, setGoalDate] = useState(null); // Date instance
  const [goalCalendarOpen, setGoalCalendarOpen] = useState(false);
  const [goalSaving, setGoalSaving] = useState(false);

  // Capital History period selector (30d / 60d / 90d / 6mo / 1y / All / custom)
  const [capitalPeriod, setCapitalPeriod] = useState(30); // days; Infinity = All
  const [customDays, setCustomDays] = useState('');

  const sortedBalance = useMemo(
    () => [...balanceHistory].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [balanceHistory]
  );

  const filteredBalance = useMemo(() => {
    if (!sortedBalance.length || capitalPeriod === Infinity) return sortedBalance;
    const latest = new Date(sortedBalance[sortedBalance.length - 1].date);
    const cutoff = new Date(latest);
    cutoff.setDate(cutoff.getDate() - capitalPeriod);
    return sortedBalance.filter((s) => new Date(s.date) >= cutoff);
  }, [sortedBalance, capitalPeriod]);

  // Pace tied to the currently-selected Capital History period.
  // (Old version recomputed avg/day per selected period — restore that.)
  const paceCurrent = useMemo(() => {
    const win = filteredBalance;
    if (!win || win.length < 2) return null;
    const first = win[0], last = win[win.length - 1];
    const days = Math.max(1, (new Date(last.date) - new Date(first.date)) / 86400000);
    return (last.balance - first.balance) / days;
  }, [filteredBalance]);

  const periodLabel = capitalPeriod === Infinity ? 'all time'
    : capitalPeriod === 180 ? '6 months'
    : capitalPeriod === 365 ? '1 year'
    : `${capitalPeriod} days`;

  const paceLabel = capitalPeriod === Infinity ? 'over all time' : `over last ${periodLabel}`;

  const capitalProj = useMemo(() => {
    if (!goalData?.goal) return null;
    const live = goalData.live_capital ?? 0;
    const target = goalData.goal.target_amount;
    const delta = target - live;
    if (delta <= 0) return { done: true, live, target };
    if (!paceCurrent || paceCurrent <= 0) return { done: false, flat: true, delta };
    const daysNeeded = delta / paceCurrent;
    const pd = new Date();
    pd.setDate(pd.getDate() + Math.ceil(daysNeeded));
    const projected = format(pd, 'yyyy-MM-dd');
    const targetDate = goalData.goal.target_date || null;
    const onTrack = targetDate ? projected <= targetDate : null;
    return { done: false, flat: false, delta, pace: paceCurrent, projected, onTrack, targetDate, paceLabel };
  }, [goalData, paceCurrent, paceLabel]);

  const fetchAll = useCallback(async () => {
    try {
      const [exRes, anRes, bhRes, goalRes] = await Promise.all([
        axios.get(`${API}/expenses`),
        axios.get(`${API}/expenses/analytics`),
        axios.get(`${API}/balance/history`),
        axios.get(`${API}/capital/goal`),
      ]);
      setExpenses(exRes.data);
      setAnalytics(anRes.data);
      setBalanceHistory(bhRes.data || []);
      setGoalData(goalRes.data || null);
    } catch (e) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load. Runs as a promise chain so state updates happen in async
  // callbacks (not synchronously inside the effect).
  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(`${API}/expenses`),
      axios.get(`${API}/expenses/analytics`),
      axios.get(`${API}/balance/history`),
      axios.get(`${API}/capital/goal`),
    ])
      .then(([exRes, anRes, bhRes, goalRes]) => {
        if (!active) return;
        setExpenses(exRes.data);
        setAnalytics(anRes.data);
        setBalanceHistory(bhRes.data || []);
        setGoalData(goalRes.data || null);
      })
      .catch(() => { if (active) toast.error('Failed to load expenses'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const dynamicCategories = useMemo(() => {
    const set = new Set(BASE_CATEGORIES);
    customCategories.forEach(c => { if (c) set.add(c); });
    expenses.forEach(e => { if (e.category) set.add(e.category); });
    return Array.from(set);
  }, [expenses, customCategories]);

  const openAdd = () => {
    setEditingExpense(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setForm({
      category: expense.category, description: expense.description,
      amount: String(expense.amount), date: expense.date ? new Date(expense.date) : new Date(),
      related_inventory_id: expense.related_inventory_id || '', notes: expense.notes || '',
      payment_source: expense.payment_source || 'cash',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.category || !form.description || !form.amount) {
      toast.error('Category, Description, and Amount are required');
      return;
    }
    const payload = {
      category: form.category, description: form.description,
      amount: parseFloat(form.amount),
      date: form.date instanceof Date ? format(form.date, 'yyyy-MM-dd') : form.date,
      related_inventory_id: form.related_inventory_id || null,
      notes: form.notes,
      payment_source: form.payment_source || 'cash',
    };
    try {
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success('Expense added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error('Failed to save expense');
    }
  };

  const handleDelete = async (expense) => {
    setExpenses(prev => prev.filter(x => x.id !== expense.id));
    let undone = false;
    toast(`Deleted "${expense.description}"`, {
      action: {
        label: 'Undo',
        onClick: () => { undone = true; setExpenses(prev => [expense, ...prev]); },
      },
      duration: 4000,
    });
    setTimeout(async () => {
      if (undone) return;
      try {
        await axios.delete(`${API}/expenses/${expense.id}`);
        fetchAll();
      } catch (e) {
        toast.error('Failed to delete');
        setExpenses(prev => [expense, ...prev]);
      }
    }, 4000);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { setBulkConfirmOpen(false); return; }
    try {
      const res = await axios.post(`${API}/expenses/bulk-delete`, { ids });
      toast.success(`Deleted ${res.data.deleted} expense${res.data.deleted === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setBulkConfirmOpen(false);
      fetchAll();
    } catch (e) {
      toast.error('Bulk delete failed');
    }
  };

  const handleLoadNotes = async () => {
    setLoadingNotes(true);
    try {
      const res = await axios.post(`${API}/expenses/load-extracted-notes`);
      const d = res.data || {};
      toast.success(`Loaded ${d.inserted} expenses · Business $${(d.business_total || 0).toLocaleString()} · Personal $${(d.personal_total || 0).toLocaleString()}`);
      setLoadNotesOpen(false);
      fetchAll();
    } catch (e) {
      toast.error('Failed to load saved notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleSheetImport = async () => {
    if (!sheetUrl.trim()) {
      toast.error('Paste a Google Sheets URL first');
      return;
    }
    setImporting(true);
    setLastImport(null);
    try {
      const endpoint = importMode === 'balance'
        ? `${API}/expenses/import-balance-sheet`
        : `${API}/expenses/import-sheet`;
      let payload;
      if (importMode === 'balance') {
        const fd = new FormData();
        fd.append('sheet_url', sheetUrl.trim());
        fd.append('replace_all', replaceAll ? 'true' : 'false');
        fd.append('year', String(balanceYear || new Date().getFullYear()));
        payload = { data: fd, headers: { 'Content-Type': 'multipart/form-data' } };
      } else {
        payload = { data: { sheet_url: sheetUrl.trim(), replace_all: replaceAll } };
      }
      const res = await axios.post(endpoint, payload.data, { headers: payload.headers });
      setLastImport(res.data);
      if (importMode === 'balance') {
        toast.success(`Imported ${res.data.snapshots} balance points · ${res.data.expenses} expense entries`);
      } else {
        const parts = [`Imported ${res.data.imported} expenses`];
        if (res.data.deleted > 0) parts.push(`replaced ${res.data.deleted} existing`);
        if (res.data.skipped > 0) parts.push(`skipped ${res.data.skipped}`);
        toast.success(parts.join(' · '));
      }
      fetchAll();
      const okCount = importMode === 'balance' ? res.data.snapshots : res.data.imported;
      if (okCount > 0 && (!res.data.total_errors || res.data.total_errors === 0)) {
        setTimeout(() => setImportOpen(false), 1000);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to import from sheet';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('replace_all', replaceAll ? 'true' : 'false');
    if (importMode === 'balance') {
      fd.append('year', String(balanceYear || new Date().getFullYear()));
    }
    setImporting(true);
    setLastImport(null);
    try {
      const endpoint = importMode === 'balance'
        ? `${API}/expenses/import-balance-sheet`
        : `${API}/expenses/import-csv`;
      const res = await axios.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLastImport(res.data);
      if (importMode === 'balance') {
        toast.success(`Imported ${res.data.snapshots} balance points`);
      } else {
        toast.success(`Imported ${res.data.imported} expenses`);
      }
      fetchAll();
      const okCount = importMode === 'balance' ? res.data.snapshots : res.data.imported;
      if (okCount > 0 && (!res.data.total_errors || res.data.total_errors === 0)) {
        setTimeout(() => setImportOpen(false), 1000);
      }
    } catch (err) {
      toast.error('Failed to import');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleQuickAdd = async () => {
    const amt = parseFloat(quickAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter the amount you spent');
      return;
    }
    setQuickSaving(true);
    try {
      const payload = {
        category: quickCategory,
        description: quickDescription || quickCategory,
        amount: amt,
        date: format(new Date(), 'yyyy-MM-dd'),
        related_inventory_id: null,
        notes: '',
        payment_source: quickSource,
      };
      await axios.post(`${API}/expenses`, payload);
      toast.success(`-$${amt.toLocaleString()} deducted from your ${quickSource === 'bank' ? 'bank account' : 'cash'}`);
      setQuickAmount('');
      setQuickDescription('');
      fetchAll();
    } catch (e) {
      toast.error('Failed to add expense');
    } finally {
      setQuickSaving(false);
    }
  };

  const handleClearBalanceHistory = async () => {
    if (!window.confirm('Wipe all balance snapshots and their derived expense entries? This is undoable.')) return;
    try {
      const res = await axios.delete(`${API}/balance/history`);
      toast.success(`Cleared ${res.data.snapshots_deleted} snapshots · ${res.data.expenses_deleted} balance-derived expenses`);
      fetchAll();
    } catch (e) {
      toast.error('Failed to clear');
    }
  };

  const openGoalDialog = () => {
    const g = goalData?.goal;
    setGoalAmount(g ? String(g.target_amount) : '');
    setGoalDate(g?.target_date ? new Date(g.target_date + 'T00:00:00') : null);
    setGoalDialogOpen(true);
  };

  const handleSaveGoal = async () => {
    const amt = parseFloat(goalAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a target amount');
      return;
    }
    setGoalSaving(true);
    try {
      const body = { target_amount: amt };
      if (goalDate instanceof Date) {
        body.target_date = format(goalDate, 'yyyy-MM-dd');
      }
      await axios.post(`${API}/capital/goal`, body);
      toast.success(`Goal set: $${amt.toLocaleString()}${body.target_date ? ` by ${body.target_date}` : ''}`);
      setGoalDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save goal');
    } finally {
      setGoalSaving(false);
    }
  };

  const handleClearGoal = async () => {
    try {
      await axios.delete(`${API}/capital/goal`);
      toast.success('Goal cleared');
      setGoalDialogOpen(false);
      fetchAll();
    } catch {
      toast.error('Failed to clear goal');
    }
  };

  const filtered = expenses.filter(e =>
    (filterCategory === 'all' || e.category === filterCategory) &&
    (typeFilter === 'all' || (e.expense_type || 'personal') === typeFilter)
  );
  const totalFiltered = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  // Chart data
  const weeklyData = analytics?.weekly || [];
  const monthlyData = analytics?.monthly || [];
  const byCategory = analytics?.by_category || [];
  const summary = analytics?.summary || {};

  // Profit vs Expenses comparison (last 6 months)
  const monthlyPnL = useMemo(() => {
    // Backend doesn't return monthly profit; derive from monthly expenses + summary.
    // Simple approach: show monthly expenses + a stretched indicator of net.
    return monthlyData.slice(-6).map((m) => ({
      label: m.label,
      Expenses: m.amount,
    }));
  }, [monthlyData]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" /></div>;
  }

  const grossProfit = summary.gross_profit || 0;
  const realizedProfit = summary.realized_profit != null ? summary.realized_profit : grossProfit;
  const netProfit = summary.net_profit || 0;
  const businessNetProfit = summary.business_net_profit != null ? summary.business_net_profit : netProfit;
  const cashAfterSpending = summary.cash_after_spending || 0;
  const taxableProfit = summary.taxable_profit != null ? summary.taxable_profit : businessNetProfit;
  const taxRate = summary.tax_rate || 0;
  const estimatedTax = summary.estimated_tax || 0;
  const afterTaxProfit = summary.after_tax_profit != null ? summary.after_tax_profit : businessNetProfit;
  const totalExpensesAll = summary.total_expenses || 0;
  const businessTotal = summary.business_total || 0;
  const personalTotal = summary.personal_total || 0;
  const ratio = summary.expense_to_profit_ratio;
  const ratioPct = ratio != null ? Math.round(ratio * 100) : null;

  const allFilteredSelected = filtered.length > 0 && filtered.every(e => selectedIds.has(e.id));
  const someFilteredSelected = filtered.some(e => selectedIds.has(e.id)) && !allFilteredSelected;

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide">EXPENSES</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {expenses.length} expenses · Lifetime total: <span className="font-semibold">{fmtMoney(totalExpensesAll)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-expenses-btn">
                <Upload className="h-4 w-4" /> Import
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => { setImportMode('expenses'); setImportOpen(true); }}
                data-testid="open-import-expenses-row"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Import Expenses (Date · Description · Amount)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setImportMode('balance'); setImportOpen(true); }}
                data-testid="open-import-balance-row"
              >
                <LineChartIcon className="h-4 w-4 mr-2" />
                Import Balance History (daily snapshots)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setLoadNotesOpen(true)}
                data-testid="open-load-notes-row"
              >
                <Database className="h-4 w-4 mr-2" />
                Load my saved notes (Jan–May 2026)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openAdd} data-testid="add-expense-btn" className="gap-2">
            <Plus className="h-4 w-4" /> Add Expense
          </Button>
        </div>
      </div>

      {/* === Money-Movement Summary Cards === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="stat-7d">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Last 7 Days</span>
              <TrendingDown className="h-4 w-4 text-amber-600" />
            </div>
            <div className="text-2xl font-semibold tabular-nums">{fmtMoney(summary.spent_7d)}</div>
          </CardContent>
        </Card>
        <Card data-testid="stat-30d">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Last 30 Days</span>
              <TrendingDown className="h-4 w-4 text-amber-600" />
            </div>
            <div className="text-2xl font-semibold tabular-nums">{fmtMoney(summary.spent_30d)}</div>
          </CardContent>
        </Card>
        <Card data-testid="stat-90d">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Last 90 Days</span>
              <TrendingDown className="h-4 w-4 text-amber-600" />
            </div>
            <div className="text-2xl font-semibold tabular-nums">{fmtMoney(summary.spent_90d)}</div>
          </CardContent>
        </Card>
        <Card data-testid="stat-net-profit" className={businessNetProfit >= 0 ? 'border-emerald-500/30' : 'border-amber-500/40'}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Business Net Profit</span>
              {businessNetProfit >= 0
                ? <TrendingUp className="h-4 w-4 text-emerald-600" />
                : <TrendingDown className="h-4 w-4 text-amber-600" />}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${businessNetProfit >= 0 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="business-net-profit">
              {fmtMoney(businessNetProfit)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Realized {fmtMoney(realizedProfit)} − Business exp {fmtMoney(businessTotal)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* === Business vs Personal split === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card data-testid="stat-business" className="border-sky-500/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Business Expenses</span>
              <Briefcase className="h-4 w-4 text-sky-600" />
            </div>
            <div className="text-2xl font-semibold tabular-nums text-sky-700 dark:text-sky-400" data-testid="business-total">
              {fmtMoney(businessTotal)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-tight">
              Matches: gas · ads · shipping · fees · supplies · packaging · tools
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-personal" className="border-amber-500/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Personal / Overall</span>
              <Wallet className="h-4 w-4 text-amber-600" />
            </div>
            <div className="text-2xl font-semibold tabular-nums text-amber-700 dark:text-amber-400" data-testid="personal-total">
              {fmtMoney(personalTotal)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-tight">
              Everything that doesn&apos;t match a business keyword
            </div>
          </CardContent>
        </Card>
      </div>

      {/* === Profit & Tax Breakdown === */}
      <Card data-testid="profit-tax-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-display tracking-wide">
            <TrendingUp className="h-4 w-4 text-primary" /> PROFIT &amp; TAX
          </CardTitle>
          <CardDescription>
            Realized profit (sold phones only) minus business expenses. Personal spending never reduces profit or tax.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div data-testid="pt-realized">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Realized Profit</div>
              <div className="text-xl font-semibold tabular-nums mt-1 text-emerald-600">{fmtMoney(realizedProfit)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Sold phones only</div>
            </div>
            <div data-testid="pt-business-net">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Business Net Profit</div>
              <div className={`text-xl font-semibold tabular-nums mt-1 ${businessNetProfit >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{fmtMoney(businessNetProfit)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">− Business expenses {fmtMoney(businessTotal)}</div>
            </div>
            <div data-testid="pt-cash-after">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cash After Spending</div>
              <div className="text-xl font-semibold tabular-nums mt-1">{fmtMoney(cashAfterSpending)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">− Personal {fmtMoney(personalTotal)} · not actual account cash</div>
            </div>
            <div data-testid="pt-taxable">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Taxable Profit</div>
              <div className="text-xl font-semibold tabular-nums mt-1">{fmtMoney(taxableProfit)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">= Business net profit</div>
            </div>
            <div data-testid="pt-estimated-tax">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Est. Self-Employment Tax ({taxRate}%)</div>
              <div className="text-xl font-semibold tabular-nums mt-1 text-amber-600">{fmtMoney(estimatedTax)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Simplified estimate — not full federal/state liability</div>
            </div>
            <div data-testid="pt-after-tax">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">After-Tax Profit</div>
              <div className={`text-xl font-semibold tabular-nums mt-1 ${afterTaxProfit >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{fmtMoney(afterTaxProfit)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">− Estimated tax</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === Capital Impact Strip === */}
      <Card data-testid="capital-impact-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-display tracking-wide">
            <Wallet className="h-4 w-4 text-primary" /> CAPITAL IMPACT
          </CardTitle>
          <CardDescription>How expenses are moving against your money</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Available Cash</div>
              <div className="text-xl font-semibold tabular-nums mt-1">{fmtMoney(summary.available_cash)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Money In Phones</div>
              <div className="text-xl font-semibold tabular-nums mt-1">{fmtMoney(summary.money_in_phones)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Overall Capital</div>
              <div className="text-xl font-semibold tabular-nums mt-1">{fmtMoney(summary.overall_capital)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Expense / Profit</div>
              <div className={`text-xl font-semibold tabular-nums mt-1 ${ratioPct == null ? 'text-muted-foreground' : ratioPct > 50 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {ratioPct == null ? '—' : `${ratioPct}%`}
              </div>
            </div>
          </div>
          {grossProfit > 0 && (
            <>
              <div className="text-xs text-muted-foreground mb-2">
                Of every $1 you profit, you spend <span className="font-semibold">${ratio != null ? ratio.toFixed(2) : '0.00'}</span> on expenses.
              </div>
              <div className="w-full h-2 rounded-full bg-muted overflow-hidden flex">
                <div
                  className="bg-amber-500 h-full"
                  style={{ width: `${Math.min(100, Math.max(0, (ratio || 0) * 100))}%` }}
                  data-testid="ratio-bar"
                />
                <div className="bg-emerald-500 h-full flex-1" />
              </div>
              <div className="flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                <span>Spent</span><span>Kept as profit</span>
              </div>
            </>
          )}
          {grossProfit <= 0 && (
            <div className="text-sm text-muted-foreground">
              Record a sale (or fill in actual price + sold date on an inventory item) to start seeing your profit impact here.
            </div>
          )}
        </CardContent>
      </Card>

      {/* === Quick Expense + Capital History Row === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Expense widget */}
        <Card data-testid="quick-expense-card" className="lg:col-span-1 border-amber-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display tracking-wide">
              <Zap className="h-4 w-4 text-amber-600" /> QUICK EXPENSE
            </CardTitle>
            <CardDescription>Auto-deducts from your capital</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount Spent</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground font-display">$</span>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={quickAmount}
                  onChange={(e) => setQuickAmount(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); }}
                  placeholder="0"
                  className="text-2xl h-14 pl-9 font-bold tabular-nums"
                  data-testid="quick-amount-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
                <Select value={quickCategory} onValueChange={setQuickCategory}>
                  <SelectTrigger className="h-9 mt-1" data-testid="quick-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {dynamicCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); openNewCategory('quick'); }}
                      className="w-full text-left text-sm text-primary font-semibold px-2 py-2 mt-1 border-t border-border hover:bg-accent rounded-sm cursor-pointer"
                      data-testid="quick-add-new-category"
                    >
                      + Add new category…
                    </button>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">For (optional)</Label>
                <Input
                  value={quickDescription}
                  onChange={(e) => setQuickDescription(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); }}
                  placeholder={quickCategory}
                  className="h-9 mt-1 text-sm"
                  data-testid="quick-description"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Deduct From</Label>
              <Select value={quickSource} onValueChange={setQuickSource}>
                <SelectTrigger className="h-9 mt-1" data-testid="quick-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash" data-testid="quick-source-cash">Cash on hand</SelectItem>
                  <SelectItem value="bank" data-testid="quick-source-bank">Bank account</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleQuickAdd}
              disabled={!quickAmount || quickSaving}
              className="w-full gap-2"
              data-testid="quick-add-btn"
            >
              <Plus className="h-4 w-4" />
              {quickSaving ? 'Adding...' : (quickAmount ? `Deduct $${parseFloat(quickAmount || 0).toLocaleString()}` : 'Add Expense')}
            </Button>
            <p className="text-[11px] text-muted-foreground leading-tight">
              Tip: dated as today. To edit or change the amount later, scroll down to the table → 3-dot menu → Edit.
            </p>
          </CardContent>
        </Card>

        {/* Capital History timeline */}
        <Card data-testid="capital-history-card" className="lg:col-span-2">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2 text-base font-display tracking-wide">
                <LineChartIcon className="h-4 w-4 text-primary" /> CAPITAL HISTORY
              </CardTitle>
              <CardDescription>
                {balanceHistory.length === 0
                  ? 'Import your daily-balance Google Sheet to see your money over time.'
                  : `${balanceHistory.length} dated snapshots${capitalPeriod !== Infinity ? ` (showing ${filteredBalance.length} from last ${periodLabel})` : ''}${paceCurrent != null ? ` · ${paceCurrent >= 0 ? '+' : ''}$${paceCurrent.toFixed(2)}/day ${paceLabel}` : ''}`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant={goalData?.goal ? 'default' : 'outline'}
                size="sm"
                onClick={openGoalDialog}
                className="gap-1.5 h-8"
                data-testid="set-goal-btn"
              >
                <Target className="h-3.5 w-3.5" />
                {goalData?.goal ? `$${goalData.goal.target_amount.toLocaleString()} goal` : 'Set goal'}
              </Button>
              {balanceHistory.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearBalanceHistory}
                  className="text-muted-foreground hover:text-destructive gap-1 text-xs h-8"
                  data-testid="clear-balance-btn"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {balanceHistory.length === 0 ? (
              <div className="h-[210px] flex flex-col items-center justify-center text-sm text-muted-foreground gap-3 border-2 border-dashed border-border rounded-lg">
                <Database className="h-8 w-8 opacity-30" />
                <p>No balance history yet</p>
                <Button
                  variant="outline" size="sm"
                  onClick={() => { setImportMode('balance'); setImportOpen(true); }}
                  data-testid="empty-import-balance-btn"
                  className="gap-2"
                >
                  <Upload className="h-3 w-3" /> Import Balance Sheet
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-1 mb-3" data-testid="capital-period-selector">
                  {[{ l: '30d', v: 30 }, { l: '60d', v: 60 }, { l: '90d', v: 90 }, { l: '6mo', v: 180 }, { l: '1y', v: 365 }, { l: 'All', v: Infinity }].map((p) => (
                    <Button
                      key={p.l}
                      size="sm"
                      variant={capitalPeriod === p.v ? 'default' : 'outline'}
                      className="h-7 px-2.5 text-xs"
                      onClick={() => { setCapitalPeriod(p.v); setCustomDays(''); }}
                      data-testid={`capital-period-${p.l}`}
                    >
                      {p.l}
                    </Button>
                  ))}
                  <div className="flex items-center gap-1 ml-1">
                    <Input
                      type="number"
                      min="1"
                      value={customDays}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCustomDays(v);
                        const n = parseInt(v, 10);
                        if (n && n > 0) setCapitalPeriod(n);
                      }}
                      placeholder="e.g. 34"
                      className="h-7 w-20 text-xs"
                      data-testid="capital-period-custom"
                    />
                    <span className="text-xs text-muted-foreground">days</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={210}>
                  <LineChart data={filteredBalance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      formatter={(v, name) => name === 'balance' ? [fmtMoney(v), 'Balance'] : [v, name]}
                      labelFormatter={(d) => `Date: ${d}`}
                    />
                    {goalData?.goal && (
                      <ReferenceLine
                        y={goalData.goal.target_amount}
                        stroke="#0EA5E9"
                        strokeDasharray="6 4"
                        strokeWidth={2}
                        label={{
                          value: `Goal: $${goalData.goal.target_amount.toLocaleString()}`,
                          position: 'insideTopRight',
                          fontSize: 10,
                          fill: '#0EA5E9',
                        }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke={goalData?.on_track === false ? '#EF4444' : '#10B981'}
                      strokeWidth={2}
                      dot={{ r: 2.5, fill: goalData?.on_track === false ? '#EF4444' : '#10B981' }}
                      activeDot={{ r: 4 }}
                      isAnimationActive
                    />
                  </LineChart>
                </ResponsiveContainer>

                {/* Goal projection text */}
                {capitalProj && (
                  <div
                    className={`mt-3 rounded-md px-3 py-2 text-xs flex items-start gap-2 ${
                      capitalProj.done || capitalProj.onTrack === true
                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                        : capitalProj.onTrack === false || capitalProj.flat
                          ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30'
                          : 'bg-muted/30 text-muted-foreground border border-border'
                    }`}
                    data-testid="goal-projection"
                  >
                    {capitalProj.done || capitalProj.onTrack === true
                      ? <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                    <div className="flex-1 leading-relaxed">
                      {capitalProj.done
                        ? <><strong>Already there!</strong> Current capital ${capitalProj.live.toLocaleString()} ≥ goal ${capitalProj.target.toLocaleString()}.</>
                        : capitalProj.flat
                          ? <>
                              <strong>${capitalProj.delta.toLocaleString()} to go.</strong>{' '}
                              Your balance has been flat or declining {paceLabel} — sell or cut expenses to start trending toward the goal.
                            </>
                          : <>
                              <strong>${Math.round(capitalProj.delta).toLocaleString()} to go.</strong>{' '}
                              At your {capitalProj.paceLabel || 'recent'} pace (+${capitalProj.pace.toFixed(2)}/day) you&apos;ll hit it around{' '}
                              <strong>{capitalProj.projected}</strong>
                              {capitalProj.targetDate && ` · target ${capitalProj.targetDate}`}
                              {capitalProj.onTrack === true ? ' · ✓ On track!' :
                               capitalProj.onTrack === false ? ' · falling behind' : ''}.
                            </>
                      }
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* === Charts row === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly bar chart */}
        <Card data-testid="weekly-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display tracking-wide">WEEKLY SPENDING</CardTitle>
            <CardDescription>Last 12 weeks</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--muted) / 0.5)' }}
                  formatter={(v) => [fmtMoney(v), 'Spent']}
                />
                <Bar dataKey="amount" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly bar chart */}
        <Card data-testid="monthly-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display tracking-wide">MONTHLY SPENDING</CardTitle>
            <CardDescription>Last 12 months</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--muted) / 0.5)' }}
                  formatter={(v) => [fmtMoney(v), 'Spent']}
                />
                <Bar dataKey="amount" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* By category pie */}
        <Card data-testid="category-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display tracking-wide">BY CATEGORY</CardTitle>
            <CardDescription>Where every dollar goes</CardDescription>
          </CardHeader>
          <CardContent>
            {byCategory.length === 0 ? (
              <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">
                No expenses yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={byCategory}
                    dataKey="amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {byCategory.map((entry, idx) => (
                      <Cell key={entry.category} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: 11 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Profit vs Expenses */}
        <Card data-testid="profit-vs-expenses-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display tracking-wide">PROFIT vs EXPENSES</CardTitle>
            <CardDescription>Snapshot of your money flow</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-emerald-600 font-medium">Gross Profit</span>
                  <span className="tabular-nums font-semibold">{fmtMoney(grossProfit)}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: '100%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-amber-600 font-medium">Total Expenses</span>
                  <span className="tabular-nums font-semibold">{fmtMoney(totalExpensesAll)}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500"
                    style={{ width: `${grossProfit > 0 ? Math.min(100, (totalExpensesAll / Math.max(grossProfit, totalExpensesAll)) * 100) : (totalExpensesAll > 0 ? 100 : 0)}%` }}
                  />
                </div>
              </div>
              <div className="pt-3 border-t">
                <div className="flex justify-between text-sm mb-1">
                  <span className={netProfit >= 0 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
                    Net (after expenses)
                  </span>
                  <span className={`tabular-nums font-bold ${netProfit >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {fmtMoney(netProfit)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* === Filter row === */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="expense-category-filter">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {dynamicCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="expense-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {filtered.length} shown · {fmtMoney(totalFiltered)}
          </span>
        </div>
        {selectedIds.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            className="gap-2"
            onClick={() => setBulkConfirmOpen(true)}
            data-testid="bulk-delete-expenses-btn"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete {selectedIds.size}
          </Button>
        )}
      </div>

      {/* === Expense Table === */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-1">No expenses found</p>
              <p className="text-sm">Add your first expense or import from Google Sheets to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allFilteredSelected ? true : (someFilteredSelected ? 'indeterminate' : false)}
                      onCheckedChange={(c) => {
                        if (c) setSelectedIds(new Set(filtered.map(e => e.id)));
                        else setSelectedIds(new Set());
                      }}
                      data-testid="select-all-expenses"
                    />
                  </TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(expense => (
                  <TableRow
                    key={expense.id}
                    data-testid={`expense-row-${expense.id}`}
                    className={selectedIds.has(expense.id) ? 'bg-destructive/5' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(expense.id)}
                        onCheckedChange={() => toggleSelect(expense.id)}
                        data-testid={`select-expense-${expense.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={CATEGORY_COLORS[expense.category] || 'bg-slate-500/10 text-slate-600'}>
                        {expense.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{expense.description}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{fmtMoney(expense.amount)}</TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">{expense.date?.slice(0, 10)}</TableCell>
                    <TableCell>
                      {expense.source === 'google_sheet_import' ? (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <Sparkles className="h-2.5 w-2.5" /> Sheet
                        </Badge>
                      ) : expense.payment_source === 'bank' ? (
                        <Badge variant="outline" className="gap-1 text-[10px]" data-testid={`expense-source-badge-${expense.id}`}>
                          <Landmark className="h-2.5 w-2.5" /> Bank
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-[10px]" data-testid={`expense-source-badge-${expense.id}`}>
                          <Wallet className="h-2.5 w-2.5" /> Cash
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`expense-actions-${expense.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(expense)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(expense)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* === Add / Edit Dialog === */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider">{editingExpense ? 'EDIT EXPENSE' : 'ADD EXPENSE'}</DialogTitle>
            <DialogDescription>{editingExpense ? 'Update the details below.' : 'Track a new expense — repair cost, shipping, etc.'}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="select-expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {dynamicCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); openNewCategory('form'); }}
                      className="w-full text-left text-sm text-primary font-semibold px-2 py-2 mt-1 border-t border-border hover:bg-accent rounded-sm cursor-pointer"
                      data-testid="form-add-new-category"
                    >
                      + Add new category…
                    </button>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" data-testid="input-expense-amount" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Deduct From</Label>
              <Select value={form.payment_source} onValueChange={v => setForm({...form, payment_source: v})}>
                <SelectTrigger data-testid="select-expense-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash" data-testid="expense-source-cash">Cash on hand</SelectItem>
                  <SelectItem value="bank" data-testid="expense-source-bank">Bank account</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What was this expense for?" data-testid="input-expense-description" />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="expense-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.date instanceof Date ? format(form.date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={form.date} onSelect={(d) => { if (d) { setForm({...form, date: d}); setCalendarOpen(false); }}} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes..." data-testid="input-expense-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-expense-btn">Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-expense-btn">{editingExpense ? 'Update' : 'Add Expense'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Bulk delete confirm === */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              DELETE {selectedIds.size} EXPENSE{selectedIds.size === 1 ? '' : 'S'}?
            </DialogTitle>
            <DialogDescription>This permanently removes the selected rows.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} data-testid="bulk-cancel-expenses">Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} data-testid="bulk-confirm-expenses">
              Delete {selectedIds.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Load my saved notes (confirm) === */}
      <Dialog open={loadNotesOpen} onOpenChange={setLoadNotesOpen}>
        <DialogContent className="max-w-md" data-testid="load-notes-dialog">
          <DialogHeader>
            <DialogTitle>Load my saved notes</DialogTitle>
            <DialogDescription>
              This loads your full itemized expense set for <strong>Jan–May 2026</strong>
              {' '}(147 items, already split into Business / Personal) into this account.
              It <strong>replaces all current expenses</strong> on this account. Use this to
              restore your data on any device or account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoadNotesOpen(false)} data-testid="load-notes-cancel">Cancel</Button>
            <Button onClick={handleLoadNotes} disabled={loadingNotes} data-testid="load-notes-confirm">
              {loadingNotes ? 'Loading…' : 'Replace & Load 147 expenses'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Import Dialog (Google Sheets URL + CSV upload) === */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setSheetUrl(''); setReplaceAll(false); setLastImport(null); } }}>
        <DialogContent className="max-w-lg" data-testid="import-expenses-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider">
              {importMode === 'balance' ? 'IMPORT BALANCE HISTORY' : 'IMPORT EXPENSES'}
            </DialogTitle>
            <DialogDescription>
              {importMode === 'balance'
                ? "Paste your 'Balance Jan 18th to May 19th'-style Google Sheet. We'll record each daily snapshot AND auto-create expense rows from the diffs (including 'Borrow +/-$X' notes)."
                : 'Paste a Google Sheets URL or upload a CSV — Date, Description, Amount (and optional Category) columns are auto-detected.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Google Sheets URL</Label>
              <Input
                placeholder="https://docs.google.com/spreadsheets/d/..../edit"
                value={sheetUrl}
                onChange={e => setSheetUrl(e.target.value)}
                data-testid="sheet-url-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Tip: in Google Sheets → File → Share → Anyone with the link, then paste the URL here.
              </p>
            </div>

            {importMode === 'balance' && (
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={balanceYear}
                  onChange={(e) => setBalanceYear(parseInt(e.target.value) || new Date().getFullYear())}
                  className="w-32"
                  data-testid="balance-year-input"
                />
                <p className="text-[11px] text-muted-foreground">
                  Your sheet has dates like &quot;Jan 18th&quot; without a year. Pick the year these dates belong to.
                </p>
              </div>
            )}

            <label
              className={`flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${replaceAll ? 'border-destructive bg-destructive/5' : 'border-border hover:bg-muted/40'}`}
            >
              <Checkbox checked={replaceAll} onCheckedChange={(c) => setReplaceAll(!!c)} className="mt-0.5" data-testid="replace-all-expenses-checkbox" />
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-2">
                  {importMode === 'balance'
                    ? 'Replace existing balance history'
                    : 'Replace all existing expenses'}
                  {replaceAll && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {replaceAll
                    ? (importMode === 'balance'
                      ? <span className="text-destructive">All current balance snapshots + their derived expenses will be wiped before importing.</span>
                      : <span className="text-destructive">All {expenses.length} expense{expenses.length === 1 ? '' : 's'} will be deleted before importing.</span>)
                    : 'New rows will be added on top of what you already have.'}
                </div>
              </div>
            </label>

            <div className="flex gap-2">
              <Button onClick={handleSheetImport} disabled={importing || !sheetUrl.trim()} className="flex-1 gap-2" data-testid="run-sheet-import-btn">
                <Sparkles className="h-4 w-4" />
                {importing ? 'Importing…' : 'Pull from Google Sheets'}
              </Button>
            </div>

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground tracking-widest">or upload CSV</span>
              </div>
            </div>

            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
              <FileSpreadsheet className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <label className="cursor-pointer">
                <span className="text-sm font-medium text-primary hover:underline">
                  {importing ? 'Working…' : 'Choose CSV file'}
                </span>
                <input
                  type="file" accept=".csv" className="hidden"
                  onChange={handleCsvUpload}
                  disabled={importing}
                  data-testid="expense-csv-input"
                />
              </label>
            </div>

            {lastImport && (
              <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1" data-testid="last-import-summary">
                {importMode === 'balance' ? (
                  <>
                    <div><strong>{lastImport.snapshots}</strong> balance snapshots saved</div>
                    <div><strong>{lastImport.expenses}</strong> derived expense rows created</div>
                  </>
                ) : (
                  <>
                    <div><strong>{lastImport.imported}</strong> imported</div>
                    {lastImport.deleted > 0 && <div><strong>{lastImport.deleted}</strong> existing removed (replace)</div>}
                    {lastImport.skipped > 0 && <div><strong>{lastImport.skipped}</strong> skipped (missing amount)</div>}
                  </>
                )}
                {lastImport.total_errors > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-destructive">{lastImport.total_errors} errors</summary>
                    <ul className="ml-4 mt-1 list-disc">
                      {(lastImport.errors || []).slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* === Capital Goal Dialog === */}
      <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
        <DialogContent data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" /> CAPITAL GOAL
            </DialogTitle>
            <DialogDescription>
              Set a target — the chart will show a dotted line and tell you whether you&apos;re on pace to hit it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest">Target Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-muted-foreground font-display">$</span>
                <Input
                  type="number"
                  step="100"
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(e.target.value)}
                  placeholder="5000"
                  className="text-xl h-12 pl-9 font-semibold tabular-nums"
                  data-testid="goal-amount-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest">Target Date (optional)</Label>
              <Popover open={goalCalendarOpen} onOpenChange={setGoalCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="goal-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {goalDate instanceof Date ? format(goalDate, 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={goalDate || undefined}
                    onSelect={(d) => { if (d) { setGoalDate(d); setGoalCalendarOpen(false); }}}
                    initialFocus
                  />
                  <div className="flex justify-end p-2 border-t bg-muted/30">
                    <Button variant="ghost" size="sm" onClick={() => setGoalDate(null)} data-testid="goal-date-clear">Clear</Button>
                  </div>
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-muted-foreground">
                Leave blank to just track the goal amount without a deadline.
              </p>
            </div>

            {/* Live projection preview */}
            {goalData?.daily_change != null && parseFloat(goalAmount) > 0 && (
              <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs space-y-1" data-testid="goal-preview">
                <div className="font-semibold">Projection (preview)</div>
                <div>Current capital: <span className="tabular-nums">{fmtMoney(goalData.live_capital)}</span></div>
                <div>Pace: <span className="tabular-nums">{goalData.daily_change >= 0 ? '+' : ''}${goalData.daily_change.toFixed(2)}/day</span></div>
                {goalData.daily_change > 0
                  ? <div>You&apos;d hit ${parseFloat(goalAmount).toLocaleString()} around{' '}
                      <strong>{(() => {
                        const days = Math.max(0, Math.ceil((parseFloat(goalAmount) - goalData.live_capital) / goalData.daily_change));
                        const d = new Date(); d.setDate(d.getDate() + days);
                        return format(d, 'yyyy-MM-dd');
                      })()}</strong>
                    </div>
                  : <div className="text-amber-600">Your balance is flat or declining — you&apos;ll need to sell more or cut expenses.</div>
                }
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {goalData?.goal && (
              <Button variant="outline" onClick={handleClearGoal} className="mr-auto gap-2 text-destructive border-destructive/40 hover:bg-destructive/5" data-testid="goal-delete-btn">
                <XIcon className="h-4 w-4" /> Remove Goal
              </Button>
            )}
            <Button variant="ghost" onClick={() => setGoalDialogOpen(false)} data-testid="goal-cancel-btn">Cancel</Button>
            <Button onClick={handleSaveGoal} disabled={!goalAmount || goalSaving} data-testid="goal-save-btn">
              {goalSaving ? 'Saving…' : (goalData?.goal ? 'Update Goal' : 'Set Goal')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Category Dialog */}
      <Dialog open={newCatDialogOpen} onOpenChange={setNewCatDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="new-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider">ADD NEW CATEGORY</DialogTitle>
            <DialogDescription>Pick a short name (e.g. Storage, Subscriptions). It’ll appear in the dropdown for next time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Category name</Label>
            <Input
              autoFocus
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveNewCategory(); }}
              placeholder="e.g. Subscriptions"
              data-testid="new-category-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewCatDialogOpen(false)} data-testid="new-category-cancel">Cancel</Button>
            <Button onClick={saveNewCategory} disabled={!newCatName.trim()} data-testid="new-category-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
